// MP219 — Empty state primitive.
//
// Replaces the pattern of "rounded-md border border-dashed py-8
// text-center with an icon + line of muted text" rebuilt inline on
// every page that has a list. Includes a CTA slot so the empty state
// can guide the operator forward instead of being a dead end.

import * as React from 'react';
import { cn } from '../lib/cn';

export interface EmptyStateProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Icon node (lucide-react component, instantiated by caller). */
  icon?: React.ReactNode;
  title: React.ReactNode;
  /** Optional supporting copy. Should answer "why is this empty + what
   *  do I do about it" in one sentence. */
  description?: React.ReactNode;
  /** Optional action node — usually a Button. Renders below the
   *  description with appropriate spacing. */
  action?: React.ReactNode;
  /** Layout density. */
  size?: 'sm' | 'md' | 'lg';
}

export const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  (
    {
      className,
      icon,
      title,
      description,
      action,
      size = 'md',
      ...props
    },
    ref,
  ) => {
    const padding =
      size === 'sm' ? 'p-6' : size === 'lg' ? 'p-16' : 'p-10';
    const iconSize =
      size === 'sm' ? 'h-6 w-6' : size === 'lg' ? 'h-12 w-12' : 'h-8 w-8';
    return (
      <div
        ref={ref}
        role="status"
        className={cn(
          'flex flex-col items-center justify-center gap-2 rounded-md border border-dashed text-center',
          padding,
          className,
        )}
        {...props}
      >
        {icon && (
          <div className={cn('text-muted-foreground', iconSize)} aria-hidden="true">
            {icon}
          </div>
        )}
        <div className="text-sm font-medium">{title}</div>
        {description && (
          <p className="max-w-md text-xs text-muted-foreground">{description}</p>
        )}
        {action && <div className="mt-2">{action}</div>}
      </div>
    );
  },
);
EmptyState.displayName = 'EmptyState';
